import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { availabilityService } from '../services/availabilityService'
import { SmartSuggestion } from '../types/availability'
import {
  SparklesIcon,
  ClockIcon,
  CalendarDaysIcon,
  LightBulbIcon,
  XMarkIcon,
  ChartBarIcon,
  UserIcon,
  BoltIcon
} from '@heroicons/react/24/outline'

interface SmartSchedulingSuggestionsProps {
  isOpen: boolean
  onClose: () => void
  onSelectSuggestion: (suggestion: SmartSuggestion) => void
  preferredDate?: string
  duration?: number
  clientId?: string
}

const SmartSchedulingSuggestions = ({
  isOpen,
  onClose,
  onSelectSuggestion,
  preferredDate,
  duration = 120,
  clientId
}: SmartSchedulingSuggestionsProps) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [suggestions, setSuggestions] = useState<SmartSuggestion[]>([])
  const [selectedSuggestion, setSelectedSuggestion] = useState<SmartSuggestion | null>(null)

  useEffect(() => {
    if (isOpen && user?.id) {
      loadSuggestions()
    }
  }, [isOpen, user?.id, preferredDate, duration, clientId])

  const loadSuggestions = async () => {
    try {
      setLoading(true)
      const smartSuggestions = await availabilityService.getSmartSuggestions(
        user!.id,
        preferredDate,
        duration,
        clientId
      )
      setSuggestions(smartSuggestions)
    } catch (error) {
      console.error('Error loading smart suggestions:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectSuggestion = (suggestion: SmartSuggestion) => {
    onSelectSuggestion(suggestion)
    onClose()
  }

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-400'
    if (confidence >= 75) return 'text-blue-400'
    if (confidence >= 60) return 'text-yellow-400'
    return 'text-orange-400'
  }

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 90) return '🎯'
    if (confidence >= 75) return '⭐'
    if (confidence >= 60) return '👍'
    return '⚠️'
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00')
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow'
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      })
    }
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="relative w-full max-w-4xl max-h-[90vh] glass-mockup rounded-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <SparklesIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Smart Scheduling Suggestions</h2>
              <p className="text-gray-400 text-sm">AI-powered optimal time slots for your shoot</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-white">Analyzing your schedule...</p>
                <p className="text-gray-400 text-sm mt-2">Finding the perfect time slots</p>
              </div>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-12">
              <LightBulbIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-white text-lg font-medium mb-2">No suggestions available</h3>
              <p className="text-gray-400">
                Try adjusting your availability settings or selecting a different time range.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Search Parameters */}
              <div className="flex flex-wrap items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="flex items-center space-x-2 text-sm text-gray-300">
                  <ClockIcon className="w-4 h-4" />
                  <span>{duration} minutes</span>
                </div>
                {preferredDate && (
                  <div className="flex items-center space-x-2 text-sm text-gray-300">
                    <CalendarDaysIcon className="w-4 h-4" />
                    <span>Around {formatDate(preferredDate)}</span>
                  </div>
                )}
                {clientId && (
                  <div className="flex items-center space-x-2 text-sm text-gray-300">
                    <UserIcon className="w-4 h-4" />
                    <span>Client preferences considered</span>
                  </div>
                )}
              </div>

              {/* Suggestions Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {suggestions.map((suggestion, index) => (
                  <motion.div
                    key={suggestion.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`glass-mockup rounded-xl p-6 cursor-pointer transition-all hover:bg-white/10 ${
                      selectedSuggestion?.id === suggestion.id
                        ? 'border-2 border-purple-500'
                        : 'border border-white/10'
                    }`}
                    onClick={() => setSelectedSuggestion(suggestion)}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                          <span className="text-white text-sm font-bold">#{index + 1}</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{formatDate(suggestion.date)}</p>
                          <p className="text-gray-400 text-sm">
                            {formatTime(suggestion.startTime)} - {formatTime(suggestion.endTime)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{getConfidenceIcon(suggestion.confidence)}</span>
                          <span className={`font-bold ${getConfidenceColor(suggestion.confidence)}`}>
                            {suggestion.confidence}%
                          </span>
                        </div>
                        <p className="text-gray-400 text-xs">confidence</p>
                      </div>
                    </div>

                    {/* Reason */}
                    <p className="text-gray-300 text-sm mb-4">{suggestion.reason}</p>

                    {/* Factors */}
                    <div className="space-y-2">
                      <h4 className="text-white font-medium text-sm">Optimization Factors:</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { key: 'availabilityMatch', label: 'Availability', icon: ClockIcon },
                          { key: 'workloadBalance', label: 'Workload', icon: ChartBarIcon },
                          { key: 'clientPreference', label: 'Client Fit', icon: UserIcon },
                          { key: 'energyLevel', label: 'Energy', icon: BoltIcon }
                        ].map((factor) => {
                          const Icon = factor.icon
                          const score = suggestion.factors[factor.key as keyof typeof suggestion.factors]
                          return (
                            <div key={factor.key} className="flex items-center space-x-2">
                              <Icon className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-400">{factor.label}</span>
                              <div className="flex-1 bg-white/10 rounded-full h-1.5">
                                <div 
                                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-full rounded-full transition-all"
                                  style={{ width: `${score}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-400 w-8">{score}%</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    {/* Select Button */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleSelectSuggestion(suggestion)
                      }}
                      className="w-full mt-4 p-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all"
                    >
                      Select This Time
                    </motion.button>
                  </motion.div>
                ))}
              </div>

              {/* Help Text */}
              <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                <div className="flex items-start space-x-3">
                  <SparklesIcon className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div>
                    <h4 className="text-blue-400 font-medium mb-1">How Smart Suggestions Work</h4>
                    <p className="text-gray-300 text-sm">
                      Our AI analyzes your working hours, past booking patterns, energy levels throughout the day, 
                      and client preferences to recommend the optimal time slots. Higher confidence scores indicate 
                      better matches with your productivity patterns and preferences.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && suggestions.length > 0 && (
          <div className="flex items-center justify-between p-6 border-t border-white/10">
            <div className="text-sm text-gray-400">
              {suggestions.length} suggestions found • Based on your availability preferences
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              
              {selectedSuggestion && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSelectSuggestion(selectedSuggestion)}
                  className="px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium transition-colors"
                >
                  Use Selected Time
                </motion.button>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default SmartSchedulingSuggestions 